import { Box, Flex, Paper, Text } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { LoadingAnimation } from 'src/UI/app-container/chat/components/LoadingAnimation';
import styles from './ProcessingSidebarAnimation.module.css';

export type SummaryItem = {
  id: string;
  label: string;
  value: string;
  extraLabel?: string;
};

type ProcessingSidebarAnimationProps = {
  items: SummaryItem[];
  clientFirstName?: string;
  documentLabel: string;
  estimatedTime?: string;
  isSubmitting: boolean;
  navigateTo: string;
};

const ANIMATION_START_DELAY = 1000;
const ANIMATION_DURATION = 1000;
const HOLD_DURATION = 2000;

export function ProcessingSidebarAnimation({
  items,
  clientFirstName,
  documentLabel,
  estimatedTime,
  isSubmitting,
  navigateTo,
}: ProcessingSidebarAnimationProps) {
  const navigate = useNavigate();

  const [animatingIndex, setAnimatingIndex] = useState(-1);
  const [animationComplete, setAnimationComplete] = useState(false);

  useEffect(() => {
    if (items.length === 0) {
      setAnimationComplete(true);
      return;
    }

    const startTimer = window.setTimeout(() => {
      setAnimatingIndex(0);
    }, ANIMATION_START_DELAY);

    return () => window.clearTimeout(startTimer);
  }, [items.length]);

  useEffect(() => {
    if (animatingIndex < 0) return;
    if (animatingIndex >= items.length) {
      setAnimationComplete(true);
      return;
    }

    const nextTimer = window.setTimeout(() => {
      setAnimatingIndex(i => i + 1);
    }, ANIMATION_DURATION);

    return () => window.clearTimeout(nextTimer);
  }, [animatingIndex, items.length]);

  useEffect(() => {
    if (!animationComplete) return;
    if (isSubmitting) return;

    const navigateTimer = window.setTimeout(() => {
      navigate(navigateTo);
    }, HOLD_DURATION);

    return () => window.clearTimeout(navigateTimer);
  }, [animationComplete, isSubmitting, navigateTo, navigate]);

  return (
    <Flex direction="column" h="100%" pos="relative" align="center" style={{ overflow: 'hidden' }}>
      <Paper
        radius="xl"
        p="md"
        mb="lg"
        pos="relative"
        w={374}
        bg="rgba(0, 0, 0, 0.35)"
        style={{
          zIndex: 10,
          flexShrink: 0,
          backdropFilter: 'blur(48px)',
          border: '1px solid rgba(253,253,253,0.15)',
        }}
      >
        <Flex justify="flex-start" mb="xs">
          <LoadingAnimation color="brand" size={24} />
        </Flex>
        <Text size="xs" fw={FONT_WEIGHTS.bold} c="white">
          {animationComplete ? 'Generating report.' : 'Taking instruction.'}
        </Text>
        <Flex justify="space-between" align="center" mt="xxxs">
          <Text size="md" fw={FONT_WEIGHTS.bold} c="white">
            {documentLabel} for {clientFirstName}
          </Text>
          {estimatedTime && (
            <Text size="md" fw={FONT_WEIGHTS.bold} c="white">
              {estimatedTime}
            </Text>
          )}
        </Flex>
      </Paper>

      <Flex
        direction="column"
        flex={1}
        justify="center"
        align="center"
        gap="xxxs"
        px="md"
        mih={0}
        style={{ overflow: 'hidden' }}
      >
        {items.map((item, index) => (
          <Box
            key={item.id}
            w={374}
            className={index <= animatingIndex ? styles.itemAnimating : undefined}
          >
            <SummaryRow label={item.label} value={item.value} extraLabel={item.extraLabel} />
          </Box>
        ))}
      </Flex>
    </Flex>
  );
}

function SummaryRow({
  label,
  value,
  extraLabel,
}: {
  label: string;
  value: string;
  extraLabel?: string;
}) {
  return (
    <Paper
      radius="xl"
      p="xxs"
      bg="rgba(0, 0, 0, 0.2)"
      style={{
        backdropFilter: 'blur(48px)',
        border: '1px solid rgba(253,253,253,0.15)',
      }}
    >
      <Flex align="center" justify="space-between" px="md" pt="xs">
        <Text size="xs" fw={FONT_WEIGHTS.medium} c="white">
          {label}
        </Text>
        {extraLabel && (
          <Text size="xs" fw={FONT_WEIGHTS.medium} c="white" opacity={0.5}>
            {extraLabel}
          </Text>
        )}
      </Flex>
      <Text size="md" fw={FONT_WEIGHTS.bold} c="white" px="md" pb="xs" truncate>
        {value}
      </Text>
    </Paper>
  );
}
